import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  AppBar,
  Toolbar,
  Typography,
  Box,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Button,
  CircularProgress,
  Divider,
  TextField,
  InputAdornment,
  Chip,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { customerService } from '../services/api';
import { Search as SearchIcon, Check as CheckIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const TransferLeadDialog = ({ open, onClose, lead, onTransferSuccess }) => {
  const { t } = useTranslation();
  const { collaborator: currentCollaborator } = useAuth();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [selectedCollaborator, setSelectedCollaborator] = useState(null);
  const [transferring, setTransferring] = useState(false);
  const [query, setQuery] = useState('');

  const { data: collabData, isLoading: loading } = useQuery({
    queryKey: ['collaborators'],
    // Only active collaborators can take a lead (the list also returns
    // pending/invited ones for the collaborators admin page).
    queryFn: () =>
      customerService.getCollaborators({
        paginationModel: { page: 0, pageSize: 100 },
        filterModel: { items: [{ field: 'status', operator: '=', value: 'active' }] },
      }),
    enabled: open,
  });

  const collaborators = useMemo(() => collabData?.data?.results ?? [], [collabData]);

  const filtered = useMemo(() => {
    const list = collaborators.filter((c) => c.uuid !== currentCollaborator?.uuid);
    if (!query) return list;
    const q = query.toLowerCase();
    return list.filter(c =>
      (c.name || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q) ||
      (c.role || '').toLowerCase().includes(q)
    );
  }, [collaborators, query, currentCollaborator?.uuid]);

  useEffect(() => {
    if (open) {
      setSelectedCollaborator(null);
    }
  }, [open]);

  const handleTransfer = async () => {
    if (!selectedCollaborator || !lead) return;

    try {
      setTransferring(true);
      const data = {
        to_collaborator_id: selectedCollaborator.uuid,
        to_collaborator_name: selectedCollaborator.name || selectedCollaborator.email,
      };
      await customerService.transferConversation(lead.uuid, data);
      onTransferSuccess();
      onClose();
    } catch (error) {
      console.error('Error transferring lead:', error);
    } finally {
      setTransferring(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={!isDesktop}
      PaperProps={isDesktop ? { sx: { width: 840, maxWidth: '90vw', height: '85vh', borderRadius: 3, overflow: 'hidden', display: 'flex', flexDirection: 'column' } } : { sx: { display: 'flex', flexDirection: 'column' } }}
    >
      <AppBar sx={{ position: 'relative', bgcolor: 'primary.main' }}>
        <Toolbar>
          <Typography sx={{ ml: 1, flex: 1 }} variant="h6" component="div">
            {t('leads.transferLead')}
          </Typography>
          {selectedCollaborator && (
            <Chip
              label={selectedCollaborator.name || selectedCollaborator.email}
              size="small"
              sx={(theme) => ({
                bgcolor: theme.alpha((theme.vars || theme).palette.primary.contrastText, 0.2),
                color: 'primary.contrastText',
              })}
            />
          )}
        </Toolbar>
      </AppBar>
      {/* Lead summary + search */}
      <Box sx={{ bgcolor: 'background.paper', p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t('leads.selectedLead')}
        </Typography>
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          {lead?.name || t('notAvailable')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('leads.selectCollaborator')}
        </Typography>

        <TextField
          fullWidth
          placeholder={t('leads.searchCollaborator') || 'Search collaborator'}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          size="small"
        />
      </Box>

      {/* Scrollable Collaborators List */}
      <Box sx={{ flex: 1, overflow: 'auto', bgcolor: 'background.default' }}>
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {t('leads.loadingCollaborators')}
            </Typography>
          </Box>
        ) : collaborators.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="body2" color="text.secondary">
              {t('leads.noCollaboratorsAvailable')}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ bgcolor: 'background.paper' }}>
            <List sx={{ p: 0 }}>
              {filtered.map((collaborator, index) => (
                <React.Fragment key={collaborator.uuid}>
                  <ListItemButton
                    selected={selectedCollaborator?.uuid === collaborator.uuid}
                    onClick={() => setSelectedCollaborator(collaborator)}
                    sx={{
                      py: 2,
                      px: 3,
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar
                        sx={{
                          // Não selecionado: fundo padrão do Avatar do MUI.
                          bgcolor: selectedCollaborator?.uuid === collaborator.uuid ? 'primary.main' : undefined,
                          width: 48,
                          height: 48,
                        }}
                      >
                        {getInitials(collaborator.name)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle2">
                          {collaborator.name || t('notAvailable')}
                        </Typography>
                      }
                      secondary={
                        <>
                          <Typography
                            component="span"
                            variant="body2"
                            color="text.secondary"
                            sx={{ display: 'block' }}
                          >
                            {collaborator.role || t('leads.noRoleDefined')}
                          </Typography>
                          <Typography
                            component="span"
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mt: 0.5 }}
                          >
                            {collaborator.email}
                          </Typography>
                        </>
                      }
                    />
                    {selectedCollaborator?.uuid === collaborator.uuid && (
                      <CheckIcon color="primary" />
                    )}
                  </ListItemButton>
                  {index < filtered.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          bgcolor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          p: 2,
          display: 'flex',
          gap: 2,
          ...(isDesktop ? { px: 3 } : {}),
        }}
      >
        <Button
          variant="outlined"
          fullWidth
          onClick={onClose}
          disabled={transferring}
          size="large"
          sx={{ py: 1.5 }}
        >
          {t('leads.cancel')}
        </Button>
        <Button
          variant="contained"
          fullWidth
          onClick={handleTransfer}
          disabled={!selectedCollaborator || transferring}
          size="large"
          sx={{ py: 1.5 }}
        >
          {transferring ? <CircularProgress size={24} color="inherit" /> : t('leads.transfer')}
        </Button>
      </Box>
    </Dialog>
  );
};

export default TransferLeadDialog;
